import { useMemo, useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './theme';
import Dashboard from '@/pages/Dashboard';
import SimulationView from '@/pages/SimulationView';
import ResultsView from '@/pages/ResultsView';
import NotificationSystem from '@/components/NotificationSystem';
import AuthRequired from '@/components/AuthRequired';
import { useSessionStore } from '@/stores';
import { parseEdgeHubLaunchParams, setEdgeHubToken, decodeJwtClaims } from '@/services/api';

function App() {
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    // Load theme preference from localStorage, default to dark
    const saved = localStorage.getItem('theme-mode');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });
  const [authChecked, setAuthChecked] = useState(false);
  // Track whether we found a valid token — this is set once during the
  // initial useEffect and used as the auth gate. We don't rely on the
  // Zustand store subscription (edgeHubToken) for the gate because the
  // persist middleware's hydration can fire after the URL-parser sets
  // the token, overwriting it with empty sessionStorage state in a new tab.
  const [hasValidToken, setHasValidToken] = useState(false);

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  // Phase 10g: pull the EdgeHub JWT from the launch URL on first mount.
  useEffect(() => {
    const launch = parseEdgeHubLaunchParams();
    console.log('[App] parseEdgeHubLaunchParams =>', launch ? `token=${launch.token.substring(0,20)}... scenario=${launch.scenario}` : 'null');
    if (launch) {
      // Fresh arrival from EdgeHub — extract JWT from URL params
      setEdgeHubToken(launch.token);
      const claims = decodeJwtClaims(launch.token);
      if (claims) {
        useSessionStore.getState().setEdgeHubContext(
          launch.token,
          claims.userId,
          claims.name
        );
      }
      if (launch.scenario) {
        useSessionStore.getState().setScenarioFromUrl(launch.scenario);
      }
      setHasValidToken(true);
    } else {
      // Page refresh — URL params are gone, but the Zustand persist middleware
      // rehydrates edgeHubToken from sessionStorage. Sync it back to the API
      // module so the request interceptor can attach the Authorization header.
      const restoredToken = useSessionStore.getState().edgeHubToken;
      if (restoredToken) {
        setEdgeHubToken(restoredToken);
        setHasValidToken(true);
      }
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme-mode', themeMode);
    // Update data-theme attribute for CSS custom properties
    document.documentElement.setAttribute('data-theme', themeMode);
    if (themeMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeMode]);

  // Provide theme toggle function via window object for global access
  // This allows components to toggle theme without prop drilling
  useEffect(() => {
    (window as any).toggleTheme = () => {
      setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
    };
    (window as any).currentTheme = themeMode;
  }, [themeMode]);

  // Block all rendering until the launch-URL parse is done, then gate
  // every route on the presence of an EdgeHub token. The brief loading
  // window prevents a flash of AuthRequired when the page is opened
  // from a valid launch URL.
  if (!authChecked) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }} />
      </ThemeProvider>
    );
  }

  if (!hasValidToken) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthRequired />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/simulation/:sessionId" element={<SimulationView />} />
          <Route path="/results/:sessionId" element={<ResultsView />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
        <NotificationSystem />
      </Box>
    </ThemeProvider>
  );
}

export default App;